import { useCallback, useMemo } from 'react';
import update from 'transmute/update';
import { SalesViewsStrategy } from 'customer-data-filters-ui/public/strategies';
import { FilterEditor, SegmentationFilters } from 'customer-data-filters-ui/public/components';
import { toDSFieldRecord, RECORD_FORMAT_TYPES } from 'customer-data-filters-ui/public/core';
import { generateRecentlyUsedPropertyGroup, RECENTLY_USED_GROUP_NAME } from '../../utils/generateRecentlyUsedPropertyGroup';
import { useIsVisibleFilterPropertyName } from '../../../properties/hooks/useIsVisibleFilterPropertyName';
import { useProperties } from '../../../properties/hooks/useProperties';
import { useRecentlyUsedPropertyNames } from '../../../recentlyUsedProperties/hooks/useRecentlyUsedPropertyNames';
import { useViewActions } from '../../../../views/hooks/useViewActions';
import { useSelectedObjectTypeDef } from '../../../../crmObjects/hooks/useSelectedObjectTypeDef';
import { useCurrentViewFilters } from '../../../../views/hooks/useCurrentViewFilters';
import { usePanelActions } from '../../../../overlay/hooks/usePanelActions';
import { useCurrentView } from '../../../../views/hooks/useCurrentView';
import { useGeneratedCachedViewById } from '../../../../views/hooks/useGeneratedCachedViewById';
import { filtersUIMessages } from './formattedMessages';
import { tracker } from '../../../../tracking/usageTracker';
import { useIsUngatedForVizSettingsM1 } from '../../../../auth/hooks/useIsUngatedForVizSettingsM1';
import { jsx as _jsx } from "react/jsx-runtime";
const emptyArray = [];

/**
 * Type definitions
 */

/**
 * @function ObjectSearchFilterEditor
 * @returns {React.ReactComponentElement}
 */
export const ObjectSearchFilterEditor = ({
  layout = 'panel'
}) => {
  // Get actions from hooks
  const {
    onFiltersChanged,
    onQuickFiltersChanged
  } = useViewActions();
  const {
    closePanel
  } = usePanelActions();
  const isUngatedForVizSettingsM1 = useIsUngatedForVizSettingsM1();

  // Get data from hooks
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'undefined' is not assignable to type '{ obje... Remove this comment to see the full error message
  const {
    objectTypeId: filterFamily
  } = useSelectedObjectTypeDef();
  const {
    filters: value,
    quickFilters
  } = useCurrentViewFilters();
  const isVisiblePropertyName = useIsVisibleFilterPropertyName();
  const properties = useProperties();
  const recentlyUsedProperties = useRecentlyUsedPropertyNames();

  // Define callbacks
  const handleFiltersChange = useCallback((newValue, opts) => {
    if (!opts.hasIncompleteFilters && !opts.hasFilterValidationErrors) {
      onFiltersChanged({
        // @ts-expect-error FIXME
        nextFilters: newValue && newValue.filterGroups || emptyArray
      });
    }
  }, [onFiltersChanged]);
  const handleQuickFiltersChange = useCallback(newQuickFiltersValue => {
    const nextFilters = newQuickFiltersValue.filterGroups[0] && newQuickFiltersValue.filterGroups[0].filters || emptyArray;
    // @ts-expect-error FIXME
    onQuickFiltersChanged({
      nextFilters
    });
  }, [onQuickFiltersChanged]);
  const recentlyUsedFieldsGroup = useMemo(() => {
    const filterGroupPropertiesName = groupProperties => groupProperties.filter(({
      name
    }) => isVisiblePropertyName(name));
    const recentlyUsedPropertiesGroup = update('properties', filterGroupPropertiesName, generateRecentlyUsedPropertyGroup(recentlyUsedProperties, properties));
    const recentlyUsedFields = toDSFieldRecord(recentlyUsedPropertiesGroup.properties, filterFamily, RECORD_FORMAT_TYPES.PROPERTY_RECORD).toList();
    return recentlyUsedPropertiesGroup.set('properties', recentlyUsedFields);
  }, [filterFamily, isVisiblePropertyName, properties, recentlyUsedProperties]);
  const IndexPageSalesViewsStrategy = useMemo(() => SalesViewsStrategy.configure({
    getFields(__filterFamily, originalFields) {
      return originalFields && originalFields.set(RECENTLY_USED_GROUP_NAME, recentlyUsedFieldsGroup);
    }
  }), [recentlyUsedFieldsGroup]);
  const crmSearchValue = useMemo(() => {
    const isEmptyFilters = value.length === 1 && !value[0].filters.length;
    return {
      filterGroups: isEmptyFilters ? [] : value,
      objectTypeId: filterFamily
    };
  }, [filterFamily, value]);
  const quickFiltersValue = useMemo(() => ({
    objectTypeId: filterFamily,
    filterGroups: [{
      filters: quickFilters
    }]
  }), [quickFilters, filterFamily]);
  const view = useCurrentView();
  const cachedView = useGeneratedCachedViewById(view.id);
  const cachedViewHasFilters = cachedView && cachedView.filterGroups.length > 0;
  const hasLocallyChangedFilters = useMemo(() => {
    return cachedView && JSON.stringify(view.filterGroups) !== JSON.stringify(cachedView.filterGroups);
  }, [view, cachedView]);
  const showEditCancelDiscardButton = hasLocallyChangedFilters || cachedViewHasFilters;
  const openInEditMode = crmSearchValue.filterGroups.length === 0;
  if (isUngatedForVizSettingsM1) {
    return /*#__PURE__*/_jsx(FilterEditor, {
      baseFilterFamily: filterFamily,
      closePanel: closePanel,
      layout: layout,
      onChange: handleFiltersChange,
      onDraftChange: handleFiltersChange,
      onQuickFiltersChange: handleQuickFiltersChange,
      openInEditMode: openInEditMode,
      disableClosingEditorPanel: true
      // @ts-expect-error FIXME
      ,
      quickFilters: quickFiltersValue,
      skipEmptyFiltersState: false,
      strategy: IndexPageSalesViewsStrategy
      // use trackerInstance prop instead and let filters handle usage tracking
      // docs: https://product.hubteam.com/docs/crm-filters/pages/filter-editor/double-panel/props.html#trackerinstance
      ,
      trackerInstance: tracker,
      uiMessages: filtersUIMessages,
      hideEditCancelDiscardButton: !showEditCancelDiscardButton,
      useNarrowPanelFormat: true
      // @ts-expect-error FIXME
      ,
      value: crmSearchValue,
      useOnePanel: true
    });
  }
  return /*#__PURE__*/_jsx(SegmentationFilters, {
    baseFilterFamily: filterFamily,
    closePanel: closePanel,
    layout: "panel",
    onChange: handleFiltersChange,
    onDraftChange: handleFiltersChange,
    onQuickFiltersChange: handleQuickFiltersChange,
    openInEditMode: openInEditMode,
    disableClosingEditorPanel: true
    // @ts-expect-error FIXME
    ,
    quickFilters: quickFiltersValue,
    skipEmptyFiltersState: true,
    strategy: IndexPageSalesViewsStrategy
    // use trackerInstance prop instead and let filters handle usage tracking
    // docs: https://product.hubteam.com/docs/crm-filters/pages/filter-editor/double-panel/props.html#trackerinstance
    ,
    trackerInstance: tracker,
    uiMessages: filtersUIMessages,
    hideEditCancelDiscardButton: !showEditCancelDiscardButton,
    useNarrowPanelFormat: true
    // @ts-expect-error FIXME
    ,
    value: crmSearchValue
  });
};